export type LowerIsBetterTrend =
  | { status: "loading" }
  | { status: "unavailable" }
  | {
      status: "ready";
      percent?: number;
      absoluteChange?: number;
      previousValue: number;
      direction: "up" | "down" | "flat";
      isGood: boolean;
    };

export const formatTrendChange = (
  trend: LowerIsBetterTrend,
  formatAbsoluteChange: (value: number) => string,
  formatPercentChange: (value: number) => string,
  noChangeText: string,
): string | undefined => {
  if (trend.status !== "ready") {
    return undefined;
  }

  if (trend.direction === "flat") {
    return noChangeText;
  }

  if (trend.absoluteChange !== undefined) {
    return formatAbsoluteChange(Math.abs(trend.absoluteChange));
  }

  return formatPercentChange(trend.percent ?? 0);
};

/** Produces a comparison trend for metrics where a smaller value is preferable. */
export const createLowerIsBetterTrend = (
  current: number | null | undefined,
  previous: number | null | undefined,
): LowerIsBetterTrend => {
  if (current == null || previous === null) return { status: "unavailable" };
  if (previous === undefined) return { status: "loading" };

  if (current === previous) {
    return {
      status: "ready",
      percent: 0,
      previousValue: previous,
      direction: "flat",
      isGood: true,
    };
  }

  if (previous === 0) {
    return {
      status: "ready",
      absoluteChange: current,
      previousValue: previous,
      direction: current > 0 ? "up" : "down",
      isGood: current < 0,
    };
  }

  const change = ((current - previous) / Math.abs(previous)) * 100;
  return {
    status: "ready",
    percent: Math.abs(change),
    previousValue: previous,
    direction: change > 0 ? "up" : "down",
    isGood: change < 0,
  };
};
